// Older files into project v2. Each migration maps fields one to one, converts units to
// the Suite's (D-006) and records what it changed as note codes (the app words them).
// GPL-3.0-or-later.
import { MPaToKPa } from "@dipfield/rock";
import type { Material, Project } from "./schema.js";
import { isObj, num, sanitize } from "./sanitize.js";

type Any = any; // eslint-disable-line @typescript-eslint/no-explicit-any

/** What a migration changed. Codes are stable; the app translates them. */
export const NOTE = {
  densityFisher: "density-method-fisher",       // retired pointwise Fisher kernel → FOI
  coneRule: "density-cone-rule",                // exact-cap rule → DIPS rule
  legacyTraverse: "legacy-single-traverse",     // settings.traverse → traverses[]
  legacyKinematics: "legacy-kinematic-mode",    // kin.mode → kinematics.enabled/show
  jcsToKPa: "jcs-mpa-to-kpa",                   // DipField Slope JCS (MPa) → kPa
  /** DipField 1.x: each set's friction (or the global one) and its tunnel cohesion became
   *  the set's strength, shared by the slope and tunnel modules (2.4.0, D-021). */
  jointStrength: "dipfield-joint-strength",
  /** Written by project 2.0.0 only; since 2.1.0 the slope view settings are kept (slope.view). */
  slopeViewDropped: "slope-view-settings-dropped",
  /** DipField Slope 0.1's toppling rules are retired (D-025): toppling uses the contact rules,
   *  so a toppling FS can differ from the one DipField Slope gave. */
  toppleContactRules: "toppling-contact-rules",
} as const;

/** DipField 1.x project ({ app: "DipField", version: 1 }). */
export function fromDipField1(j: Any, now = new Date().toISOString()): Project {
  const s: Any = isObj(j.settings) ? j.settings : {}, notes: string[] = [];
  let method = s.method;
  if (method === "fisher") { method = "fisher-overlap"; notes.push(NOTE.densityFisher); }
  let coneRule = s.coneRule;
  if (coneRule != null && coneRule !== "dips") { coneRule = "dips"; notes.push(NOTE.coneRule); }
  let traverses = Array.isArray(j.traverses) && j.traverses.length ? j.traverses : null;
  if (!traverses) {
    if (isObj(s.traverse) && s.traverse.kind) notes.push(NOTE.legacyTraverse);
    traverses = [{ id: "T1", name: "T1", tr: isObj(s.traverse) && s.traverse.kind ? s.traverse : { kind: "spot" } }];
  }
  // kinematics: DipField 1.0 { enabled, show, ... }; earlier files { mode: "planar" | "none" | ... }
  let k: Any = isObj(s.kin) ? { ...s.kin } : {};
  if ("mode" in k) {
    const m = String(k.mode);
    k = { dip: k.dip, dipDir: k.dipDir, friction: k.friction, lateral: k.lateral, noLimits: m === "planar-nolimits",
      show: m === "planar-nolimits" || m === "none" ? "planar" : m, pairs: "all", showZone: k.showZone !== false, chart: "dipDir", enabled: m !== "none" };
    notes.push(NOTE.legacyKinematics);
  }
  const tun: Any = isObj(j.tun) ? j.tun : {};
  const rose: Any = isObj(j.rose) ? j.rose : {};
  // DipField's tunnel took φ from the set (or the kinematic φ) and c from its tunnel table:
  // that is the set's strength now, for the tunnel and the slope alike
  const sets: Any[] = Array.isArray(j.sets) ? j.sets : [];
  const tj: Any = isObj(tun.joints) ? tun.joints : {};
  const materials: Record<string, Partial<Material>> = {};
  for (const set of sets) {
    if (!isObj(set) || set.id == null) continue;
    const f = Number(set.friction), c = Number(tj[set.id]?.cohesion);
    materials[String(set.id)] = { model: "mc", phi: set.friction != null && Number.isFinite(f) ? f : num(k.friction, 30), c: Number.isFinite(c) && c > 0 ? c : 0 };
  }
  if (Object.keys(materials).length) notes.push(NOTE.jointStrength);
  const savedAt = typeof j.savedAt === "string" ? j.savedAt : now;
  return sanitize({
    meta: { name: "DipField project", createdAt: savedAt, savedAt, migratedFrom: { from: "DipField", version: num(j.version, 1), at: now, notes } },
    data: { format: s.format, planes: j.planes, traverses, terzaghi: { use: s.useWeights, minBiasDeg: s.minBias } },
    sets: j.sets,
    stereo: {
      projection: s.proj,
      density: { method, countCircle: s.countCircle, coneRule },
      display: { view: "net", poles: s.showPoles, poleSize: s.poleSize, contours: s.showContour, contourStyle: s.contourStyle, overlay: s.overlay,
        globalMean: s.showMean, bestFit: s.showBest, setPlanes: s.showSetPlanes, cones: s.showCones },
      rose: { ...rose, set: rose.set === "" ? "all" : rose.set },
    },
    slope: {
      face: { dip: k.dip, dipDir: k.dipDir }, lateral: k.lateral,
      kinematics: { enabled: k.enabled, friction: k.friction, noLimits: k.noLimits, show: k.show, pairs: k.pairs, showZone: k.showZone, chart: k.chart },
      materials,
    },
    // DipField keyed the combination by set names; the Suite opens on the critical one
    tunnel: { shape: tun.shape, width: tun.width, height: tun.height, trend: tun.trend, plunge: tun.plunge, length: tun.length,
      unitWeight: tun.gamma, joints: tun.joints, combo: null },
  });
}

/** A DipField Slope material: JCS was typed in MPa, everything else is already kPa. */
function slopeMaterial(m: Any): Partial<Material> | undefined {
  if (!isObj(m)) return undefined;
  return { model: m.model, c: m.c, phi: m.phi, jrc: m.jrc, jcs: Number.isFinite(+m.jcs) ? MPaToKPa(+m.jcs) : undefined, phir: m.phir,
    a: m.a, b: m.b, pc: m.pc, d: m.d, waviness: m.wav, waterPressure: m.u, spacing: m.sp };
}

/** DipField Slope 0.1 project ({ app: "DipField Slope", version: 1, state }). */
export function fromDipFieldSlope1(j: Any, now = new Date().toISOString()): Project {
  const st: Any = isObj(j.state) ? j.state : {}, notes: string[] = [NOTE.jcsToKPa, NOTE.toppleContactRules];
  const sl: Any = isObj(st.slope) ? st.slope : {}, run: Any = isObj(st.run) ? st.run : {}, ld: Any = isObj(st.loads) ? st.loads : {};
  const caps: Any = isObj(st.caps) ? st.caps : {}, view: Any = isObj(st.view) ? st.view : {};
  const sets: Any[] = Array.isArray(st.sets) ? st.sets : [];
  const materials: Record<string, Partial<Material>> = {};
  for (const s of sets) if (isObj(s) && s.id != null) { const m = slopeMaterial(s.mat); if (m) materials[String(s.id)] = m; }
  const savedAt = typeof j.savedAt === "string" ? j.savedAt : now;
  return sanitize({
    meta: { name: "DipField Slope project", createdAt: savedAt, savedAt, migratedFrom: { from: "DipField Slope", version: num(j.version, 1), at: now, notes } },
    data: { format: "dip-dipdir", planes: st.planes },
    sets: sets.map((s: Any) => ({ id: s?.id, name: s?.name, window: s?.window, friction: null })),
    stereo: {
      projection: { method: view.ea === false ? "equal-angle" : "equal-area", hemisphere: "lower" },
      display: { poles: view.poles, contours: view.contours, setPlanes: view.setPlanes },
    },
    slope: {
      face: { dip: sl.faceDip, dipDir: sl.faceDir }, lateral: sl.lateral,
      geometry: { upper: { dip: sl.upDip, dipDir: sl.upDir }, height: sl.height, length: sl.length, unitWeight: sl.gamma, bench: sl.bench },
      materials, other: slopeMaterial(st.other),
      run: { basis: run.basis, pairs: run.pairs, planar: run.planar, wedge: run.wedge, topple: run.topple, target: run.target,
        toppleBase: run.tbase, toppleToe: run.ttoe, toppleMethod: run.tmethod },
      loads: { water: ld.water === true ? "custom" : ld.water, fill: ld.fill, seismicK: ld.k, seismicTrend: ld.ktrend, force: ld.F, forceTrend: ld.Ft, forcePlunge: ld.Fp },
      caps: { volume: caps.volume, weight: caps.weight, trace: caps.trace, persistence: caps.pers },
      view: { tab: view.tab, intersections: view.wedges, listKind: view.listKind },
    },
  });
}
